/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { jwtDecode } from 'jwt-decode';
import { TOKEN_COOKIE_NAME, TOKEN_LOCAL_STORAGE_KEY } from '../constants/globals';

const defaultAccountResult = {};

export const isLoggedIn = () => {
    // eslint-disable-next-line no-undef
    return localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY) !== null;
};

export const getAccountWithToken = () => {
    // eslint-disable-next-line no-undef
    const accountWithTokenJson = localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY);

    if (accountWithTokenJson) {
        return JSON.parse(accountWithTokenJson);
    }

    return defaultAccountResult;
};

export const getAccount = () => {
    const accountWithToken = getAccountWithToken();

    if (accountWithToken !== defaultAccountResult) {
        return accountWithToken.user;
    }

    return defaultAccountResult;
};

export const getAuthHeader = () => {
    const accountWithToken = getAccountWithToken();

    if (accountWithToken !== defaultAccountResult) {
        return `Bearer ${accountWithToken.access_token}`;
    }
    return '';
};

export const deleteAuthCookie = () => {
    const domainMatch = document.domain.match(/[^.]*\.[^.]*$/);
    const expDate = new Date();

    const cookie = {
        [TOKEN_COOKIE_NAME]: '',
        expires: expDate.toUTCString(),
        path: '/',
        domain: domainMatch ? domainMatch[0] : document.domain,
    };

    document.cookie = Object.keys(cookie)
        .map((key) => {
            return `${key}=${cookie[key]};`;
        })
        .join('')
        .concat('secure');
};

const setAuthCookie = (token) => {
    const domainMatch = document.domain.match(/[^.]*\.[^.]*$/);
    const tokenExp = jwtDecode(token).exp;
    const expDate = new Date(tokenExp * 1000);

    const cookie = {
        [TOKEN_COOKIE_NAME]: escape(token),
        expires: expDate.toUTCString(),
        path: '/',
        domain: domainMatch ? domainMatch[0] : document.domain,
    };

    const cookieStr = Object.keys(cookie)
        .map((key) => {
            return `${key}=${cookie[key]};`;
        })
        .join('')
        .concat('secure');

    document.cookie = cookieStr;
};

export const setAuthStorage = (responseObj) => {
    localStorage.setItem(TOKEN_LOCAL_STORAGE_KEY, JSON.stringify(responseObj));
    setAuthCookie(responseObj.access_token);
};

export const getTokenRemainingSeconds = (token) => {
    // Token timestamps are epoc format, stored in whole seconds
    // time remaining is however long the token was good for, minus a little extra

    const tokenObj = jwtDecode(token);
    const padding = 5; // to account for latency/etc

    return tokenObj.exp - tokenObj.iat - padding;
};

const getAudienceClaimType = (token) => {
    const decoded = jwtDecode(token);
    const audience = decoded.aud;
    return Array.isArray(audience) ? audience.pop() : audience;
};

export const claimIsInternal = (token) => {
    return getAudienceClaimType(token) === 'internal';
};

export const claimIsSingleFactor = (token) => {
    return getAudienceClaimType(token) === 'singlefactor';
};

export const claimIsResetPasswordMFA = (token) => {
    if (token) {
        return getAudienceClaimType(token) === 'ResetPasswordMFAToken';
    }
    return false;
};

export const claimIsEmailPassword = (token) => {
    return getAudienceClaimType(token) === 'ResetEmailToken';
};
